import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { ImgDb } from './model/img-db';
import { ImgDbService } from './services/img-db.service';
import { ImgDbInfo } from './model/img-db-info';
import { ImgCategory } from './model/img-category';
import { ImgWithSelection } from './model/img-with-selection';
import {
    BehaviorSubject,
    catchError,
    forkJoin,
    Observable,
    of,
    Subscription,
    switchMap,
    tap,
} from 'rxjs';
import { ImgAnnotation } from './model/img-annotation';
import { ImgDbZoomService } from './services/img-db-zoom.service';
import { CategoryWithSelection } from './model/category-with-selection';
import { AnnotationStateService } from './services/annotation-state.service';
import { ImgLabelingCanvasViewerComponent } from './img-labeling-canvas-viewer/img-labeling-canvas-viewer.component';
import { map, take } from 'rxjs/operators';
import { ImgDbUploadService } from './services/img-db-upload.service';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { ImagesStorageService } from './services/images-storage.service';
import { PagingResult } from '../shared/models/pageable';
import {
    Container,
    ContainerMembership,
} from '../shared/models/container.types';
import { ImgDbUploadUtilService } from './services/img-db-upload-util.service';
import { ProjectSplitScreenService } from '../project-split-screen/services/project-split-screen.service';
import { SplitScreenState } from '../project-split-screen/components/project-split-screen/project-split-screen.component';
import { ApplicationLockService } from '../shared/services/application-lock.service';
import { ImgCategories } from './model/img-categories';
import {
    CategoryClickedData,
    CategoryCreatedData,
    CategoryDeletedData,
    CategoryManagerAction,
    CategoryManagerActionType,
    CategoryMergeData,
    CategoryMergeParameter,
    CategoryRenameData,
    CategorySortingChangedData,
    SortingOptions,
} from './img-db-category-manager/img-db-category-manager.component';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import {
    BodyLine,
    ConfirmationDialogComponent,
    ConfirmationDialogData,
} from '../shared/components/confirmation-modal/confirmation-dialog.component';
import { FormControl } from '@angular/forms';
import { UserCacheService } from '../xtra/user-cache/user-cache-service';
import { ContainerService } from '../shared/services/container/container.service';
import { BeforeTrainingStart } from '../shared/interfaces/before-training-start';
import {
    ImageDbUploadTarget,
    TrainingUploadTarget,
    UploadErrorSummary,
    UploadTargetInfo,
} from '../shared/models/upload-job';
import { ImgDbUploadErrorStoreService } from './services/img-db-upload-error-store.service';
import { TrainingService } from '../shared/services/training.service';
import { ToastMessageService } from '../shared/services/toast-message.service';
import { isContainerTypeAllowedForImgDb } from '../shared/models/container-type-compatibility';

export enum SelectionButtonMode {
    Select = 'Select',
    Deselect = 'Deselect',
}

//Note that suffix must be always defined
export type SortingKey = { option: SortingOptions; suffix: string };

export interface TrainingContext {
    containerName: string;
    trainingName: string;
}
export type ImageSortingOptions = 'new-old' | 'old-new' | 'A-Z' | 'Z-A';

@Component({
    selector: 'app-img-db',
    templateUrl: './img-db.component.html',
    styleUrls: ['./img-db.component.scss'],
    providers: [ImagesStorageService, AnnotationStateService],
    standalone: false,
})
export class ImgDbComponent
    extends BeforeTrainingStart
    implements OnInit, OnDestroy
{
    readonly appLockMessage: string = 'appLock.categoryUpdate';

    protected readonly SplitScreenState = SplitScreenState;
    readonly MANAGE_DIALOG_PAGE_SIZE: number = 100;
    readonly LABELING_DIALOG_PAGE_SIZE: number = 8;

    navItems: string[] = ['overview', 'annotate'];

    hoveredNavItemIdx: number | null = null;
    hoveredFunctionalButtonIdx: number | null = null;

    activeViewIdx: number;
    imageDb: ImgDb;
    containerId: string;
    container: Container;
    dbInfo: ImgDbInfo;
    categories: ImgCategory[] = [];
    selectedCategoryNames: string[] = [];
    selectedEditCategory: ImgCategory;
    isEmptyCategorySelected: boolean = false;
    uncategorizedImagesCount: number;
    annotations: ImgAnnotation[];

    dbName: string = '';
    private trainingContext$ = new BehaviorSubject<TrainingContext | null>(
        null
    );
    uploadErrorSummaries: UploadErrorSummary[] = [];
    isUploadErrorModalOpen: boolean = false;

    routingData = {
        name: '',
        path: '/workspace',
    };

    categoryTree = {};

    currentImagesInRow: number;
    lengthOfImagesList: number = 0;

    selectButtonMode: SelectionButtonMode = SelectionButtonMode.Select;
    selectButtonEnabled: boolean = true;

    isPanButtonActive: boolean = false;

    @ViewChild(ImgLabelingCanvasViewerComponent)
    labelComponent: ImgLabelingCanvasViewerComponent;

    @Input() trainingId: string;
    @Input() isDbFromTraining: boolean = false;
    @Input() canEdit: boolean = true;

    currentPage: number = 1;
    imageSelectionEmpty = true;

    subscriptions: Subscription[] = [];
    zoomSubscriptions: Subscription[] = [];
    splitScreenState: SplitScreenState;
    categoriesSortingKey: SortingKey = {
        option: 'A-Z',
        suffix: '',
    };
    imageSortingOptions: ImageSortingOptions[] = [
        'new-old',
        'old-new',
        'A-Z',
        'Z-A',
    ];
    selectedImageSortingOption: FormControl<ImageSortingOptions> =
        new FormControl();
    fetchCategoriesOnViewIndexChange: boolean = false;
    isOwner: boolean;

    public constructor(
        private service: ImgDbService,
        private route: ActivatedRoute,
        private zoomService: ImgDbZoomService,
        private location: Location,
        public annotationStateService: AnnotationStateService,
        private uploadService: ImgDbUploadService,
        private uploadUtilService: ImgDbUploadUtilService,
        public imagesStorage: ImagesStorageService,
        private splitScreenStateService: ProjectSplitScreenService,
        private blockerService: ApplicationLockService,
        private translateService: TranslateService,
        private dialog: MatDialog,
        private userCacheService: UserCacheService,
        private containerService: ContainerService,
        private uploadErrorStoreService: ImgDbUploadErrorStoreService,
        private trainingService: TrainingService,
        private toastService: ToastMessageService,
        private router: Router
    ) {
        super();
    }

    override saveBeforeStart(): Observable<any> {
        return this.saveAnnotations();
    }

    ngOnInit(): void {
        if (
            !this.isDbFromTraining &&
            !isContainerTypeAllowedForImgDb(this.extractContainerTypeFromUrl())
        ) {
            this.redirectToNotFound();
            return;
        }

        if (this.isDbFromTraining) {
            this.subscriptions.push(
                this.splitScreenStateService.splitScreenStateChange$.subscribe(
                    (state: SplitScreenState) => {
                        this.splitScreenState = state;
                        this.setZoomLevel(state);
                    }
                )
            );
            if (this.trainingId) {
                this.loadTrainingInfo();
            }
        } else {
            this.setZoomLevel();
        }
        this.retrieveImageDb()
            .pipe(
                switchMap(() => {
                    return this.populateDataAfterRetrieveImageDb();
                })
            )
            .subscribe();
        this.activeViewIdx = 0;
        this.annotationStateService.$annotations.next([]);

        this.subscriptions.push(
            this.uploadService.uploadProcessState.subscribe(() => {
                // TODO: call getImages() instead.
                this.populateDataAfterRetrieveImageDb().subscribe();
            })
        );

        this.subscriptions.push(
            this.uploadService.uploadCompleted$.subscribe((imageDbId) => {
                if (imageDbId === this.imageDb?.id) {
                    this.checkForUploadErrors();
                }
            })
        );

        this.subscriptions.push(
            this.imagesStorage.selectedImage$.subscribe(
                (image: ImgWithSelection) => {
                    this.onImageSelectedForEditing(image);
                }
            )
        );
        this.subscriptions.push(
            this.imagesStorage.imagesPage$.subscribe(
                (_: PagingResult<ImgWithSelection>) => {
                    this.imageSelectionEmpty = true;
                }
            )
        );
    }

    isDeleteButtonDisabled() {
        return (
            !this.canEdit ||
            (this.activeViewIdx === 0 && this.imageSelectionEmpty) ||
            (this.activeViewIdx === 1 &&
                !this.imagesStorage.selectedImage$.value)
        );
    }

    updateState(resetPageRequired: boolean, selectedImageIndex?: number) {
        if (resetPageRequired) {
            this.imagesStorage.changePage(1);
        }

        this.populateDataAfterRetrieveImageDb()
            .pipe(
                catchError((error) => {
                    this.blockerService.releaseApplicationLock();
                    throw error;
                }),
                tap((_) => {
                    if (resetPageRequired) {
                        this.imagesStorage.selectedImage$.next(
                            this.imagesStorage.imagesPage$.value?.values[0]
                        );
                    } else if (selectedImageIndex !== undefined) {
                        if (
                            selectedImageIndex >
                            this.imagesStorage.imagesPage$.value?.values.length
                        ) {
                            this.imagesStorage.selectedImage$.next(null);
                        } else {
                            let index = selectedImageIndex;
                            if (
                                selectedImageIndex ===
                                this.imagesStorage.imagesPage$.value.values
                                    .length
                            ) {
                                index -= 1;
                            }
                            this.imagesStorage.selectedImage$.next(
                                this.imagesStorage.imagesPage$.value?.values[
                                    index
                                ]
                            );
                        }
                    }
                    this.blockerService.releaseApplicationLock();
                })
            )
            .subscribe();
    }

    renameCategory(categoryRenamedData: CategoryRenameData) {
        this.updateFiltersOnCategoryChange(
            categoryRenamedData.oldName,
            categoryRenamedData.newName
        );
        this.updateState(false);
    }

    updateFiltersOnCategoryChange(oldName: string, newName?: string): boolean {
        if (this.selectedCategoryNames.includes(oldName)) {
            this.modifySelectedCategoryNames(oldName);
            if (newName) {
                this.modifySelectedCategoryNames(newName);
            }
            return true;
        }
        return false;
    }

    mergeCategory(categoryMergeData: CategoryMergeData) {
        const filtersChanged = this.updateFiltersOnCategoryChange(
            categoryMergeData.oldName
        );
        this.annotationStateService.renameAnnotations(
            categoryMergeData.oldId,
            categoryMergeData.newId
        );
        this.updateState(filtersChanged);
    }

    private populateDataAfterRetrieveImageDb() {
        return of(null).pipe(
            switchMap(() =>
                forkJoin<[ImgCategories, PagingResult<ImgWithSelection>]>([
                    this.getCategories(this.imageDb, this.categoriesSortingKey),
                    this.getImages(),
                ])
            )
        );
    }

    retrieveImageDb(): Observable<ImgDb> {
        let observable;
        const containerId = this.extractContainerIdFromUrl();
        this.containerId = containerId;
        if (this.isDbFromTraining) {
            observable = this.service.getImageDbByTrainingId(this.trainingId);
        } else {
            observable = forkJoin<[Container, ContainerMembership]>([
                this.service.getImageDb(containerId),
                this.containerService.getMyMembership(containerId),
            ]).pipe(
                map(
                    ([container, membership]: [
                        Container,
                        ContainerMembership,
                    ]) => {
                        this.container = container;
                        this.containerId = container.id;
                        const imgDb = container.imageDb;
                        imgDb.name = container.name;
                        this.isOwner = membership.roleName === 'OWNER';
                        return imgDb;
                    }
                )
            );
        }
        return observable.pipe(
            switchMap((db: ImgDb) => {
                this.imagesStorage.setData(this.MANAGE_DIALOG_PAGE_SIZE, db.id);
                this.setImageDb(db);
                this.checkForUploadErrors();
                return this.userCacheService.loadData(
                    'imageSortingForDatabase' + db.id
                );
            }),
            tap((data: { sortingKey: ImageSortingOptions }) => {
                this.selectedImageSortingOption.setValue(data.sortingKey);
            }),
            catchError(() => {
                this.selectedImageSortingOption.setValue('new-old');
                return of({ sortingKey: 'new-old' });
            })
        );
    }

    setZoomLevel(state?: SplitScreenState) {
        this.zoomSubscriptions.forEach((subscription: Subscription) => {
            subscription.unsubscribe();
        });
        this.zoomSubscriptions = [];
        let imagesInRow: number;

        if (state === SplitScreenState.DEFAULT) {
            imagesInRow = 4;
        }
        if (state === SplitScreenState.LEFT_CLOSED) {
            imagesInRow = 6;
        }

        this.zoomService.setDefaultValue(imagesInRow);

        this.zoomSubscriptions.push(
            this.zoomService.imagesInRowManageDialog.subscribe((count) => {
                this.currentImagesInRow = count;
            })
        );

        this.zoomSubscriptions.push(
            this.zoomService.zoomLevelLabelDialog.subscribe((level) => {
                this.onZoomLevelChanged(level, false);
            })
        );
    }

    onImageSortingChange() {
        this.userCacheService
            .saveData('imageSortingForDatabase' + this.imageDb.id, {
                sortingKey: this.selectedImageSortingOption.value,
            })
            .subscribe();
        this.imagesStorage.changePage(this.imagesStorage.currentPage);
        this.getImages().subscribe();
    }

    getImages() {
        return this.imagesStorage
            .getImages(true, this.selectedImageSortingOption.value)
            .pipe(
                tap((pageable: PagingResult<ImgWithSelection>) => {
                    this.changeSelectButtonMode(SelectionButtonMode.Select);
                    this.lengthOfImagesList = pageable.values.length;
                    this.selectButtonEnabled = this.lengthOfImagesList > 0;
                })
            );
    }

    extractContainerIdFromUrl(): string {
        return this.route.snapshot.paramMap.get('containerId');
    }

    private extractContainerTypeFromUrl(): string {
        return this.route.snapshot.paramMap.get('containerType');
    }

    private redirectToNotFound(): void {
        this.router.navigate(['/not-found']);
    }

    getBackButtonName() {
        return this.imageDb ? this.imageDb.name : '';
    }

    onNavClick(idx: number, reset: boolean = true): void {
        if (idx !== this.activeViewIdx) {
            this.imageSelectionEmpty = true;
            this.fetchCategoriesOnViewIndexChange = true;
            this.activeViewIdx = idx;
            if (reset) {
                this.resetCurrentPage(true);
            }
            this.zoomService.setCurrentZoomLevel(0);
        }
        this.changeSelectButtonMode(SelectionButtonMode.Select);
    }

    onNameUpdate(val: string): void {
        this.service.updateName(this.containerId, val).subscribe((imgDb) => {
            this.setImageDb(imgDb);
        });
    }

    onSelectEditCategory(category: ImgCategory): void {
        // selection of Category in Label/Annotation Editor
        if (!category) {
            this.selectedEditCategory = null;
        } else {
            this.isPanButtonActive = false;
            if (this.selectedEditCategory === category) {
                this.toggleImageDraggingInChildComponent(
                    this.isPanButtonActive
                );
            }
            this.selectedEditCategory = category;
        }
    }

    onImageSelectedForEditing(img: ImgWithSelection) {
        if (img) {
            this.annotationStateService.fetchAnnotations(
                this.imageDb.id,
                img.id,
                this.computeNeighbourImageIds(img.id)
            );
        }
    }

    /**
     * Returns up to 20 image ids before and after the given image inside the
     * currently loaded page, used by the annotation cache to silently
     * prefetch the annotations the user is most likely to view next.
     */
    private computeNeighbourImageIds(imgId: string): string[] {
        const NEIGHBOUR_RADIUS = 20;
        const page = this.imagesStorage.imagesPage$.value;
        const imagesInPage = page?.values;
        if (!imagesInPage || imagesInPage.length === 0) {
            return [];
        }
        const selectedIdx = imagesInPage.findIndex(
            (image: ImgWithSelection) => image.id === imgId
        );
        if (selectedIdx === -1) {
            return [];
        }
        const start = Math.max(0, selectedIdx - NEIGHBOUR_RADIUS);
        const end = Math.min(
            imagesInPage.length,
            selectedIdx + NEIGHBOUR_RADIUS + 1
        );
        const neighbours: string[] = [];
        for (let i = start; i < end; i++) {
            if (i !== selectedIdx) {
                neighbours.push(imagesInPage[i].id);
            }
        }
        return neighbours;
    }

    handleCategorySelect(category: CategoryWithSelection) {
        category.active = !category.active;
        this.updateImgAnnotations(category);
    }

    handleCategoryDelete(id: number) {
        const deletedAnnotationIndex = this.annotations.findIndex(
            (annotation: ImgAnnotation) =>
                annotation.id.toString() === id.toString()
        );

        if (deletedAnnotationIndex >= 0) {
            this.annotations.splice(deletedAnnotationIndex, 1);
        }
    }

    updateImgAnnotations(category: CategoryWithSelection) {
        const selectedAnnotation = this.annotations.find(
            (annotation: ImgAnnotation) =>
                annotation.id.toString() === category.annotationId.toString()
        );
        if (selectedAnnotation) {
            selectedAnnotation.active = category.active;
        }
    }

    openImageDeletionDialog() {
        if (!this.isDeleteButtonDisabled()) {
            this.dialog
                .open<
                    ConfirmationDialogComponent,
                    ConfirmationDialogData,
                    CategoryMergeParameter
                >(ConfirmationDialogComponent, {
                    data: {
                        headerTranslationKey:
                            'imgDb.header.functionalButtons.deleteDialog.header',
                        confirmationQuestion: {
                            translationKey:
                                'imgDb.header.functionalButtons.deleteDialog.question' +
                                (this.activeViewIdx === 0
                                    ? 'Overview'
                                    : 'Annotate'),
                        },
                        confirmButtonTranslationKey:
                            'imgDb.header.functionalButtons.deleteDialog.confirm',
                        rejectButtonTranslationKey:
                            'imgDb.header.functionalButtons.deleteDialog.reject',
                        transferData: true,
                    } as ConfirmationDialogData,
                    width: '550px',
                })
                .afterClosed()
                .subscribe((transferData) => {
                    if (transferData) {
                        this.deleteImages();
                    }
                });
        }
    }

    deleteImages() {
        let imageIdsToDelete = [];
        let selectedIndex = undefined;
        if (this.activeViewIdx === 0) {
            imageIdsToDelete = this.imagesStorage.imagesPage$.value.values
                .filter((image: ImgWithSelection) => image.isSelected)
                .map((image: ImgWithSelection) => image.id);
        } else if (this.activeViewIdx === 1) {
            const selectedImageId = this.imagesStorage.selectedImage$.value.id;
            selectedIndex =
                this.imagesStorage.imagesPage$.value.values.findIndex(
                    (image: ImgWithSelection) => image.id === selectedImageId
                );
            const imagesPage = this.imagesStorage.imagesPage$.value;
            if (
                selectedIndex === 0 &&
                this.imagesStorage.currentPage === imagesPage.totalPages &&
                imagesPage.totalElements % this.imagesStorage.pageSize === 1
            ) {
                selectedIndex = this.imagesStorage.pageSize - 1;
            }
            imageIdsToDelete.push(selectedImageId);
        }
        this.blockerService.setApplicationLock();
        this.service.deleteImages(this.imageDb.id, imageIdsToDelete).subscribe({
            next: () => {
                this.updateState(false, selectedIndex);
            },
            error: () => {
                this.updateState(false);
            },
        });
    }

    updateCategoryTree(annotation: ImgAnnotation) {
        const category = this.categories.find(
            (cat) => cat.id + '' === annotation.categoryId + ''
        );
        if (annotation.active) this.categoryTree[category.name].active = true;
        this.categoryTree[category.name].items.forEach((item) => {
            if (item.annotationId.toString() === annotation.id.toString())
                item.active = annotation.active;
        });
    }

    onZoomLevelChanged(level: number, updateGlobalZoom: boolean) {
        if (level === 0) {
            this.isPanButtonActive = false;

            this.toggleImageDraggingInChildComponent(this.isPanButtonActive);
        }
        if (updateGlobalZoom) {
            this.zoomService.zoomLevelLabelDialog.next(level);
        }
    }

    private setImageDb(imgDb: ImgDb): void {
        this.imageDb = imgDb;
        this.routingData.name = imgDb.name;
        this.uploadService.setViewedImgDbId(imgDb.id);
    }

    private loadTrainingInfo() {
        this.subscriptions.push(
            this.trainingService
                .get(this.trainingId)
                .pipe(
                    switchMap((training) =>
                        this.containerService
                            .getById(this.extractContainerIdFromUrl())
                            .pipe(
                                map((container) => ({
                                    trainingName: training.name,
                                    containerName: container?.name ?? '',
                                }))
                            )
                    )
                )
                .subscribe((context) => {
                    this.trainingContext$.next(context);
                })
        );
    }

    get isTrainingInfoLoaded(): boolean {
        return (
            !this.isDbFromTraining || this.trainingContext$.getValue() !== null
        );
    }

    private getUploadTargetInfo(): UploadTargetInfo {
        if (this.isDbFromTraining) {
            const context = this.trainingContext$.getValue();
            if (!context) {
                throw new Error(
                    'Training context not loaded. Cannot create upload target.'
                );
            }
            return {
                type: 'training',
                imageDbId: this.imageDb.id,
                imageDbName: this.imageDb.name,
                containerName: context.containerName,
                containerId: this.containerId,
                trainingName: context.trainingName,
                trainingId: this.trainingId,
            } as TrainingUploadTarget;
        }
        return {
            type: 'imageDb',
            imageDbId: this.imageDb.id,
            imageDbName: this.imageDb.name,
            containerId: this.containerId,
        } as ImageDbUploadTarget;
    }

    private checkForUploadErrors() {
        if (!this.imageDb?.id) {
            return;
        }
        this.uploadErrorStoreService
            .getUploadErrorSummaries(this.imageDb.id)
            .subscribe((response) => {
                this.uploadErrorSummaries = response?.summaries ?? [];
                this.openUploadErrorModalIfNeeded();
            });
    }

    private openUploadErrorModalIfNeeded() {
        if (
            this.isUploadErrorModalOpen ||
            this.uploadErrorSummaries.length === 0
        ) {
            return;
        }
        this.isUploadErrorModalOpen = true;
        const summary = this.aggregateUploadErrorSummaries(
            this.uploadErrorSummaries
        );
        const bodyLines = this.buildUploadErrorModalLines(summary);
        this.dialog
            .open<ConfirmationDialogComponent, ConfirmationDialogData, void>(
                ConfirmationDialogComponent,
                {
                    data: {
                        headerTranslationKey: 'imgDb.upload.errorModal.header',
                        confirmationQuestion: {
                            translationKey: 'imgDb.upload.errorModal.summary',
                            interpolateParameters: {
                                count: summary.failedCount,
                            },
                        },
                        bodyLines,
                        confirmButtonTranslationKey:
                            'imgDb.upload.errorModal.confirm',
                        rejectButtonTranslationKey: null,
                        transferData: null,
                    } as ConfirmationDialogData,
                    width: '550px',
                    disableClose: true,
                    panelClass: 'upload-error-dialog',
                }
            )
            .afterClosed()
            .subscribe(() => {
                const handledIds = this.uploadErrorSummaries.map(
                    (entry) => entry.id
                );
                this.uploadErrorStoreService
                    .clearUploadErrorSummaries(this.imageDb.id, handledIds)
                    .subscribe(() => {
                        this.isUploadErrorModalOpen = false;
                        this.uploadErrorSummaries = [];
                        this.checkForUploadErrors();
                    });
            });
    }

    private aggregateUploadErrorSummaries(
        summaries: UploadErrorSummary[]
    ): UploadErrorSummary {
        return summaries.reduce(
            (total, current) => ({
                id: current.id,
                totalCount: total.totalCount + current.totalCount,
                failedCount: total.failedCount + current.failedCount,
                invalidFormatCount:
                    total.invalidFormatCount + current.invalidFormatCount,
                fileTooLargeCount:
                    total.fileTooLargeCount + current.fileTooLargeCount,
                uploadFailedCount:
                    total.uploadFailedCount + current.uploadFailedCount,
            }),
            {
                id: '',
                totalCount: 0,
                failedCount: 0,
                invalidFormatCount: 0,
                fileTooLargeCount: 0,
                uploadFailedCount: 0,
            }
        );
    }

    private buildUploadErrorModalLines(
        summary: UploadErrorSummary
    ): BodyLine[] {
        const lines: BodyLine[] = [
            {
                translationKey: 'imgDb.upload.errorModal.reasonHeader',
            },
        ];

        if (summary.fileTooLargeCount > 0) {
            lines.push({
                translationKey: 'imgDb.upload.errorModal.reason.fileTooLarge',
                interpolateParameters: { count: summary.fileTooLargeCount },
                style: 'bold-bullet',
            });
        }
        if (summary.invalidFormatCount > 0) {
            lines.push({
                translationKey: 'imgDb.upload.errorModal.reason.invalidFormat',
                interpolateParameters: { count: summary.invalidFormatCount },
                style: 'bold-bullet',
            });
        }
        if (summary.uploadFailedCount > 0) {
            lines.push({
                translationKey: 'imgDb.upload.errorModal.reason.uploadFailed',
                interpolateParameters: { count: summary.uploadFailedCount },
                style: 'bold-bullet',
            });
        }

        return lines;
    }

    back(): void {
        this.location.back();
    }

    isViewActive(idx: number): boolean {
        return idx === this.activeViewIdx;
    }

    isNameEditable(): boolean {
        return this.activeViewIdx === 0;
    }

    getA11yForName(): string {
        return 'Name of current Image Database';
    }

    onSelectAllButtonClick(isAllSelected: boolean) {
        this.service.selection.next(isAllSelected);
        if (isAllSelected) {
            this.changeSelectButtonMode(SelectionButtonMode.Deselect);
        } else {
            this.changeSelectButtonMode(SelectionButtonMode.Select);
        }
    }

    changeSelectButtonMode(mode: SelectionButtonMode) {
        this.selectButtonMode = mode;
    }

    getFiltersString(): string {
        return this.isEmptyCategorySelected
            ? [
                  this.translateService.instant('imgDb.labelManager.noLabel'),
                  ...this.selectedCategoryNames,
              ].join(' | ')
            : this.selectedCategoryNames.join(' | ');
    }

    getDbInfo(db: ImgDb) {
        this.service
            .getImageDbInfo(db.id)
            .subscribe((info) => (this.dbInfo = info));
    }

    public createAndPersistCategory(categoryCreatedData: CategoryCreatedData) {
        this.service
            .createCategory(categoryCreatedData.categoryName, this.imageDb.id)
            .pipe(
                switchMap(() =>
                    this.getCategories(this.imageDb, this.categoriesSortingKey)
                )
            )
            .subscribe();
    }

    public selectOrUnselectCategory(clickedCategoryData: CategoryClickedData) {
        if (this.activeViewIdx === 0) {
            this.modifySelectedCategoryNames(clickedCategoryData.category.name);
            this.resetCurrentPage(true);
        } else if (this.activeViewIdx === 1) {
            this.onSelectEditCategory(clickedCategoryData.category);
        }
    }

    private modifySelectedCategoryNames(clickedCategoryName: string) {
        if (!this.selectedCategoryNames.includes(clickedCategoryName)) {
            this.selectedCategoryNames.push(clickedCategoryName);
        } else {
            this.removeClickedCategoryAsSelected(clickedCategoryName);
        }
        this.imagesStorage.selectedCategoryNames = this.selectedCategoryNames;
    }

    private removeClickedCategoryAsSelected(clickedCategory: string) {
        this.selectedCategoryNames = this.selectedCategoryNames.filter(
            (category) => category !== clickedCategory
        );
    }

    public deleteCategoryAndPersist(categoryDeleteData: CategoryDeletedData) {
        let cat: ImgCategory;
        const categoryIdToDelete = categoryDeleteData.categoryId;
        this.blockerService.setApplicationLock(this.appLockMessage);
        this.saveAnnotations()
            .pipe(
                switchMap((_) => {
                    cat = this.categories.find(
                        (category) => category.id === categoryIdToDelete
                    );
                    // Check if Category still present
                    if (cat) {
                        return this.service.deleteCategory(
                            this.imageDb.id,
                            categoryIdToDelete
                        );
                    }
                    return of(null);
                })
            )
            .subscribe((_) => {
                this.annotationStateService.removeAllAnnotationsForCategory(
                    cat?.id,
                    false
                );
                const filtersChanged = this.updateFiltersOnCategoryChange(
                    cat?.name
                );
                this.updateState(filtersChanged);
            });
    }

    getCategories(db: ImgDb, sortingKey: SortingKey) {
        this.categoriesSortingKey = sortingKey;
        return this.service
            .getCategories(db.id, sortingKey.option + sortingKey.suffix)
            .pipe(
                tap((response: ImgCategories) => {
                    this.categories = response.categories;
                    this.uncategorizedImagesCount =
                        response.uncategorizedImagesCount;
                })
            );
    }

    public handleEmptyCategorySelected() {
        this.isEmptyCategorySelected = !this.isEmptyCategorySelected;
        this.imagesStorage.isEmptyCategorySelected =
            this.isEmptyCategorySelected;
        this.resetCurrentPage(true);
    }

    isSelectedImageBookmarked(): boolean {
        return !!(
            this.imagesStorage.selectedImage$.value &&
            this.imagesStorage.selectedImage$.value.isBookmarked
        );
    }

    toggleBookmark() {
        this.imagesStorage.toggleBookmark();
    }

    undo() {
        this.annotationStateService.undo();
    }

    redo() {
        this.annotationStateService.redo();
    }

    /**
     * Method receives one of three string values (see below) from html file and calls service
     * @param zoomAction has one of three values: 'in', 'out', 'default'
     * 'in' -> zoom in, each time one image in a row less
     * 'out' -> zoom out, each time one image in a row more
     * 'default' -> set to default view which is 6 images in a row
     */
    onHandleZoom(zoomAction: string) {
        if (this.lengthOfImagesList !== 0) {
            this.zoomService.handleZoom(zoomAction, this.activeViewIdx);
        }
    }

    get currentZoomLevel() {
        return this.zoomService.getCurrentZoomLevel(this.activeViewIdx);
    }

    get currentMaxZoom() {
        return this.zoomService.getCurrentMax(this.activeViewIdx);
    }

    get currentMinZoom() {
        return this.zoomService.getCurrentMin(this.activeViewIdx);
    }

    get currentDefaultZoom() {
        return this.zoomService.getCurrentDefault(this.activeViewIdx);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
        this.zoomSubscriptions.forEach((sub) => sub.unsubscribe());
        this.uploadService.setViewedImgDbId('');
        this.uploadService.cancelRefreshImages();
    }

    onPanButtonClick() {
        this.isPanButtonActive = !this.isPanButtonActive;
        this.toggleImageDraggingInChildComponent(this.isPanButtonActive);
    }

    private toggleImageDraggingInChildComponent(enable: boolean) {
        this.labelComponent?.toggleImageDragging(enable);
    }

    onItemDropped(event: NgxFileDropEntry[]) {
        if (!this.isTrainingInfoLoaded) {
            this.toastService.showWarningMsg(
                this.translateService.instant(
                    'imgDb.upload.trainingInfoLoading'
                )
            );
            return;
        }

        this.imagesStorage.selectedCategoryNames = this.selectedCategoryNames =
            [];

        this.imagesStorage.isEmptyCategorySelected =
            this.isEmptyCategorySelected = false;

        //TODO: resetCurrentPage true when subscribe on uploadProcessState removed?
        this.resetCurrentPage(false);
        this.uploadService.addToUploadQueue(
            this.imageDb.id,
            event,
            this.getUploadTargetInfo()
        );
    }

    getValidImageFormats(): string[] {
        return this.uploadUtilService.VALID_FILE_FORMATS;
    }

    /**
     *  Will split the key by '.' and take the last element and add '-button' to the result.
     * @param navItemTranslationKey is a translation key for each nav element (imgDb.navItem.Manage)
     */
    getUniqueNavItemId(navItemTranslationKey: string) {
        const parts = navItemTranslationKey.split('.');
        return parts[parts.length - 1] + '-button';
    }

    resetCurrentPage(retrieveAllImages: boolean) {
        this.imagesStorage.changePage((this.currentPage = 1));
        if (retrieveAllImages) {
            this.imagesStorage
                .getImages(true, this.selectedImageSortingOption.value)
                .pipe(take(1))
                .subscribe(() => {
                    this.imagesStorage.updateImageSelection(0);
                    this.imageSelectionEmpty = true;
                });
        }
    }

    saveAnnotations() {
        this.annotationStateService.resetActions();
        return this.annotationStateService.saveCommands(
            true,
            this.imagesStorage.selectedImage$.value?.id
        );
    }

    handleCategoryManagerAction(action: CategoryManagerAction) {
        switch (action.type) {
            case CategoryManagerActionType.CATEGORY_CREATED:
                this.createAndPersistCategory(
                    action.parameter as CategoryCreatedData
                );
                break;
            case CategoryManagerActionType.CATEGORY_DELETED:
                this.deleteCategoryAndPersist(
                    action.parameter as CategoryDeletedData
                );
                break;
            case CategoryManagerActionType.CATEGORY_CLICKED:
                this.selectOrUnselectCategory(
                    action.parameter as CategoryClickedData
                );
                break;
            case CategoryManagerActionType.EMPTY_CATEGORY_SELECTED:
                this.handleEmptyCategorySelected();
                break;
            case CategoryManagerActionType.CATEGORY_SORTING_CHANGED:
                this.getCategories(
                    this.imageDb,
                    (action.parameter as CategorySortingChangedData).sortingKey
                ).subscribe();
                break;
            case CategoryManagerActionType.CATEGORY_RENAMED:
                this.renameCategory(action.parameter as CategoryRenameData);
                break;
            case CategoryManagerActionType.CATEGORY_MERGED:
                this.mergeCategory(action.parameter as CategoryMergeData);
                break;
            case CategoryManagerActionType.UPDATE_STATE:
                this.updateState(false);
                break;
        }
    }

    onSwitchToAnnotateAtIndex(index: number) {
        this.imagesStorage.selectedImage$.next(
            this.imagesStorage.imagesPage$.value.values[index]
        );
        this.onNavClick(1, false);
    }
}
